import React, { useState, useMemo, useCallback } from 'react';

import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useIsStandard } from '@contexts/operator/PlanContext';
import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { SelectValue, MultipleValue } from '@shared/table/Rules/component';

import { valueAddedServiceService } from '@services/operator/valueAddedServiceService';

interface MerchantOption {
  id: string;
  name: string;
}

interface OperatorDataShape {
  merchants?: MerchantOption[];
  merchantId?: string;
  [key: string]: unknown;
}

interface ValueAddedServiceRow {
  id: string;
  title?: string;
  default_price?: number | string | null;
  merchant_id?: string;
  merchant_name?: string;
  created_at?: string;
  [key: string]: unknown;
}

export default function ValueAddedServicesList() {
  const { t } = useTranslation();
  const { merchants, merchantId } = useOperatorData() as OperatorDataShape;
  const isStandard = useIsStandard();
  const [lastModified, setLastModified] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ValueAddedServiceRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const merchantOptions = useMemo(() => {
    if (!merchants) return [];
    return merchants.map((merchant) => ({
      label: merchant.name,
      value: merchant.id
    }));
  }, [merchants]);

  const handleCreate = useCallback(() => {
    setEditingRecord(null);
    form.resetFields();
    // 單一 merchant 模式下 merchant_id 選擇器會被隱藏，須主動填入
    if (isStandard && merchantId) {
      form.setFieldsValue({ merchant_id: merchantId });
    }
    setModalVisible(true);
  }, [form, isStandard, merchantId]);

  const handleEdit = useCallback((record: ValueAddedServiceRow) => {
    setEditingRecord(record);
    form.setFieldsValue({
      title: record.title,
      default_price: record.default_price,
      merchant_id: record.merchant_id
    });
    setModalVisible(true);
  }, [form]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      const response = await valueAddedServiceService.destroy(id);
      if (response.success) {
        message.success(t('common.message.delete_success'));
        setLastModified(Date.now());
      } else {
        message.error(response.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      message.error(t('common.error.operation_failed'));
    }
  }, [t]);

  const handleModalSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      let response;
      if (editingRecord) {
        response = await valueAddedServiceService.update(editingRecord.id, values);
      } else {
        response = await valueAddedServiceService.create(values);
      }

      if (response.success) {
        message.success(editingRecord
          ? t('common.message.update_success')
          : t('common.message.create_success')
        );
        setModalVisible(false);
        setLastModified(Date.now());
      } else {
        message.error(response.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      if ((error as { errorFields?: unknown }).errorFields) {
        return;
      }
      message.error(t('common.error.operation_failed'));
    } finally {
      setSubmitting(false);
    }
  }, [form, editingRecord, t]);

  const handleModalCancel = useCallback(() => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  }, [form]);

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: 'models.value_added_service.title',
      persist: true,
      sortable: true,
      sortFieldName: 'merchant_value_added_services.title',
      width: '250px',
      wrap: true,
      selector: ({ title }: ValueAddedServiceRow) => title,
    },
    {
      nameKey: 'models.value_added_service.default_price',
      sortable: true,
      sortFieldName: 'merchant_value_added_services.default_price',
      width: '150px',
      selector: ({ default_price }: ValueAddedServiceRow) => default_price != null ? `$${parseFloat(String(default_price)).toFixed(2)}` : '-',
    },
    {
      nameKey: 'models.merchant.name',
      sortable: true,
      sortFieldName: 'merchants.name',
      width: '200px',
      selector: ({ merchant_name }: ValueAddedServiceRow) => merchant_name || '-',
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'merchant_value_added_services.created_at',
      width: '180px',
      selector: ({ created_at }: ValueAddedServiceRow) => created_at || '-',
    },
    {
      nameKey: 'common.actions',
      width: '120px',
      cell: (record: ValueAddedServiceRow) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          />
          <Popconfirm
            title={t('common.confirm.delete')}
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record.id);
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText={t('common.button.confirm')}
            cancelText={t('common.button.cancel')}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ], [t, handleEdit, handleDelete]);

  const supportRules = useMemo<SupportRule[]>(() => [
    // 單一 merchant 模式下不顯示 merchant 篩選器
    ...(isStandard ? [] : [{
      label: t('models.merchant.name'),
      component: SelectValue,
      inputName: 'merchant_value_added_services.merchant_id',
      defaultPinned: true,
      defaultOptions: merchantOptions,
    }]),
    {
      label: t('models.value_added_service.title'),
      component: MultipleValue,
      inputName: 'merchant_value_added_services.title',
      createable: true,
    },
  ], [t, merchantOptions, isStandard]);

  return (
    <div>
      <ListPageHeader
        items={['merchant_settings', 'merchant_settings.value_added_services']}
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'contains',
          searchFields: [t('models.value_added_service.title')],
        }}
        columnsEditable={true}
        tableId="OperatorValueAddedServicesTable"
        defaultQuery={{
          sorting_field: 'merchant_value_added_services.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={valueAddedServiceService}
      />

      <Modal
        title={editingRecord
          ? t('operator.value_added_services.modal.edit_title')
          : t('operator.value_added_services.modal.create_title')
        }
        open={modalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        confirmLoading={submitting}
        okText={t('common.button.save')}
        cancelText={t('common.button.cancel')}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
          {isStandard ? (
            <Form.Item name="merchant_id" hidden>
              <Input />
            </Form.Item>
          ) : (
            <Form.Item
              name="merchant_id"
              label={t('models.merchant.name')}
              rules={[{ required: true, message: t('operator.value_added_services.validation.merchant_required') }]}
            >
              <Select
                options={merchantOptions}
                placeholder={t('operator.value_added_services.placeholder.select_merchant')}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          )}

          <Form.Item
            name="title"
            label={t('models.value_added_service.title')}
            rules={[{ required: true, message: t('operator.value_added_services.validation.title_required') }]}
          >
            <Input placeholder={t('operator.value_added_services.placeholder.title')} />
          </Form.Item>

          <Form.Item
            name="default_price"
            label={t('models.value_added_service.default_price')}
            rules={[{ required: true, message: t('operator.value_added_services.validation.default_price_required') }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              placeholder={t('operator.value_added_services.placeholder.default_price')}
              prefix="$"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
